'use client';

import React, { useEffect, useRef, useState } from 'react';

type LazySectionProps = {
  children: React.ReactNode;
  placeholderHeight?: number;
  rootMargin?: string;
  threshold?: number;
  once?: boolean;
  className?: string;
};

export default function LazySection({
  children,
  placeholderHeight,
  rootMargin = '400px 0px',
  threshold = 0.01,
  once = true,
  className,
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (!ref.current) return;
    const element = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) observer.unobserve(element);
            break;
          }
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);

  if (!mounted) {
    return <div ref={ref} style={{ minHeight: placeholderHeight ?? 200 }} className={className} />;
  }

  return (
    <div
      ref={ref}
      className={className}
      style={
        !isVisible
          ? {
              minHeight: placeholderHeight ?? 200,
              contentVisibility: 'auto',
              contain: 'layout paint style',
            }
          : { contentVisibility: 'auto', contain: 'layout paint style' }
      }
      aria-busy={!isVisible}
      suppressHydrationWarning
    >
      {isVisible ? children : null}
    </div>
  );
}
